import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import queryString from 'query-string'

import type { CommentTopic } from 'services/content/types'
import {
  FilterBar,
  SortDropdown,
  TopicFilterDropdown,
  BackToWodLink,
} from 'ui-component-library/components/CommentsModule'
import { getCommentStrings } from 'store/strings/selectors'
import { getContentPublishDate } from 'store/pages/selectors'
import {
  formatDailyDate,
  nextDay,
  parseDateString,
} from 'ui-component-library/utils/helpers'

type Props = {
  topics: CommentTopic[]
  selectedTopic?: CommentTopic
  activeSort: string
  onSort: (sort: 'DESC' | 'ASC') => void
}

function CommentFilterBarContainer({
  onSort,
  activeSort,
  selectedTopic,
  topics,
}: Props) {
  const location = useLocation()
  const history = useHistory()

  const {
    allCommentsText,
    newestText,
    oldestText,
    sortByText,
    filterByText,
    wodText,
    backToLinkText,
  } = useSelector(getCommentStrings)
  const contentPublishDate = useSelector(getContentPublishDate)

  const relatedWodDate = useMemo(() => {
    if (contentPublishDate) {
      return formatDailyDate(nextDay(parseDateString(contentPublishDate)))
    }
  }, [contentPublishDate])

  function handleChangeTopicId(topicId: string) {
    const search = topicId ? queryString.stringify({ topicId }) : undefined

    history.push({
      pathname: location.pathname,
      search,
    })
  }

  const sortOptions = [
    { value: 'DESC', label: newestText },
    { value: 'ASC', label: oldestText },
  ]

  const filterOptions = useMemo(() => {
    const [firstTopic, ...rest] = topics

    if (rest.length === 0) {
      return null
    }

    return [
      {
        label: `${allCommentsText} ${firstTopic.title}`,
        value: '',
      },
      {
        label: `${wodText} ${firstTopic.title}`,
        value: firstTopic.topicId,
      },
      ...rest.map(({ title, topicId }) => ({
        label: title,
        value: topicId,
      })),
    ]
  }, [topics])

  return (
    <FilterBar>
      {filterOptions && (
        <TopicFilterDropdown
          onChange={handleChangeTopicId}
          labelText={filterByText}
          selectedOption={selectedTopic && selectedTopic.topicId}
          options={filterOptions}
        />
      )}
      {!filterOptions && relatedWodDate && (
        <BackToWodLink
          wodDate={relatedWodDate}
          backToLinkText={backToLinkText}
        />
      )}
      <SortDropdown
        onChange={onSort}
        options={sortOptions}
        labelText={sortByText}
        selectedOption={activeSort}
      />
    </FilterBar>
  )
}

export default CommentFilterBarContainer
